import React, {useState, useEffect, useCallback, useContext} from 'react';
import CarSelector from './car_selector';
import ComparationTable from './comparation_table';
import VincentricServices from '../service';
import {VehicleContext} from '../context/modules/VehicleContext';
import {
  CTA,
  Container,
  ContainerPadding,
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import { AppContext } from '../context/AppContext';
import { Model, Trim } from '../types/data';

const DisclaimerContainer = styled.div`
  margin-top: 4rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  img {
    margin-left: 8px;
    width: 100px;
  }
`;

interface AppLocaleService {
  localeService: any;
  trackingService: any;
  contentService: any;
}

const labelsen = require('../data/en.json');
const labelsfr = require('../data/fr.json');
const modelData = require('../data/models.json');

const MaintenanceCompare: React.FC<AppLocaleService> = ({
  localeService,
  trackingService,
  contentService,
}) => {

  const { globalApi } = useContext(AppContext);
  const [preselectedModelId, setPreselectedModelId] = useState('');
  const [preselectedYear, setPreselectedYear] = useState('');
  const [preselectedCar, setPreselectedCar] = useState('');
  const [selectedCar, setSelectedCar] = useState('');
  const [selectedTrim, setSelectedTrim] = useState('');
  const [isShowroomPage, setIsShowroomPage] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState(0);
  const [compareData, setCompareData] = useState([]);
  const [language, setLanguage] = useState('en');
  const [labels, setLabels] = useState(labelsfr);

  const [showTable, setShowTable] = useState<boolean>(false);
  const [trimDetails, setTrimDetails] = useState<Trim | null>(null);

  const vehicleCtx = useContext(VehicleContext);

  const apiBaseUrl =
    `${globalApi}/api/total-cost-of-ownership/?distance={distance}&trim_id={trim_id}`;
  const apiModelData =
    `${globalApi}/sc-model-info?sales_code={sales_code}&year={year}&lang={lang}`;

  const service = new VincentricServices();

  const _setTimer = useCallback((counter: number) => {
    let timer: any = null;
    timer = setTimeout(() => {
      let modelYear = contentService.content.year;
      let modelId = contentService.content.modelCode;

      if ((modelYear === undefined || modelId === undefined) && counter < 81) {
        clearTimeout(timer);
        _setTimer(counter + 1);
      } else {
        clearTimeout(timer);
        if (counter < 81) {
          setPreselectedModelId(modelId);
          setPreselectedYear(modelYear);
        } else {
          setIsShowroomPage(false);
        }
      }
    }, 300);
  }, []);

  useEffect(() => {
    setLabels(localeService['language'] === 'fr' ? labelsfr : labelsen);
    setLanguage(localeService['language'] === 'fr' ? 'fr' : 'en');
    _setTimer(1);
  }, [localeService, _setTimer]);

  useEffect(() => {
    if(!selectedTrim) return;
    const trimDetailsValue = (selectedCar as unknown as Model).trims.find(trim => trim.name === selectedTrim);
    setTrimDetails(trimDetailsValue as Trim);
  }, [selectedCar, selectedTrim]);

  const _compareModelHandler = async (
    distance: string,
    trim_id: string,
    car: any,
  ) => {
    let response: any;

    try {
      setSelectedCar(car);
      setSelectedDistance(parseInt(distance));
      let currentTrim = '';
      car.trims.forEach((trim: any) => {
        if (trim.id.toString() === trim_id.toString()) {
          currentTrim = trim.name.toString();
        }
      });
      setSelectedTrim(currentTrim);
      response = await service.getComparation(apiBaseUrl, distance, trim_id);
      if (response.parsedBody.data !== null) {
        const data = response.parsedBody.data.Maintenance.Vehicle;
        setCompareData(data);
      } else {
        setCompareData([]);
      }
    } catch (e) {
      console.log('Error: ', e);
    }
  };

  const _getModelName = useCallback(
    async (sales_code: string, year: string, lang: string) => {
      let response: any;
      try {
        response = await service.getModelName(
          apiModelData,
          sales_code,
          year,
          lang,
        );
        const {year: modelYear, name} = response.parsedBody.data;
        if (response.parsedBody.data !== null) {
          const car = (name + ' ' + modelYear).toLowerCase();
          setPreselectedCar(car);
        }
      } catch (e) {
        console.log('Error: ', e);
      }
    },
    [],
  );

  const _setSelectedTrim = (trim: any) => {
    setTimeout(function () {
      setSelectedTrim(trim);
    }, 1000);
  };

  useEffect(() => {
    if (preselectedModelId !== '' && preselectedYear !== '') {
      setIsShowroomPage(true);
    }
  }, [preselectedModelId, preselectedYear]);

  useEffect(() => {
    if (isShowroomPage) {
      _getModelName(preselectedModelId, preselectedYear, language);
    }
  }, [
    isShowroomPage,
    _getModelName,
    language,
    preselectedModelId,
    preselectedYear,
  ]);

  useEffect(() => {
    if (!compareData.length) return;

    vehicleCtx?.set(compareData);
  }, [compareData]);

  return (
    <Container
      data-waypoint={labels.title}
      padding={{
        left: ContainerPadding.grid002,
        right: ContainerPadding.grid002,
      }}
      stretchContent
      wrap="always"
    >
      <div id="content" className="content">
        <CarSelector
          language={language}
          labels={labels}
          models={modelData.models}
          distances={modelData.annual_distances}
          compareCallback={_compareModelHandler}
          isShowroomPage={isShowroomPage}
          preselectedCar={preselectedCar}
          setSelectedTrimOnLoad={_setSelectedTrim}
          tracking={trackingService}
          contentService={contentService}
        />
        {/* <div id="Move" /> */}

        <DisclaimerContainer>
          <CTA
            tag="button"
            emphasis="tertiary"
            onClick={() => setShowTable((prev) => !prev)}
          >
            {showTable ? labels.chartHide : labels.chartShow}
          </CTA>

          <Container>
            <Text appearance={TokenTextAppearance.copy100}>
              {labels.disclaimer}
            </Text>
            <img
              src="https://vw-canada-websites.s3.amazonaws.com/feature-apps/cost-comparison/vincentric-logo.png"
              alt="Vincentric Logo"
            />
          </Container>
        </DisclaimerContainer>

        {showTable ? (
          <ComparationTable
            trimDetails={trimDetails!}
            language={language}
            labels={labels}
            tableSource={compareData}
            selectedCar={selectedCar}
            isShowroomPage={isShowroomPage}
            trim={selectedTrim}
            distance={selectedDistance}
            tracking={trackingService}
          />
        )
          : null}
      </div>
    </Container>
  );
};

export default MaintenanceCompare;
